// Define interfaces for strategy components
export interface Condition {
  id: string;
  subject: string;
  indicator: string;
  operator: string;
  value: string;
}

export interface Action {
  id: string;
  type: string;
  asset: string;
  quantity: string;
  price?: string;
}

export interface Rule {
  id: string;
  trigger: 'IF' | 'WHEN';
  conditions: Condition[];
  actions: Action[];
}

// PineScript version for TradingView
export enum PineScriptVersion {
  V4 = 'v4',
  V5 = 'v5'
}

export interface PineScriptOptions {
  version?: PineScriptVersion;
  strategyName: string;
  description?: string;
  includeComments?: boolean;
}

/**
 * Service to convert visual strategy rules or Python code to PineScript for TradingView backtesting
 */
export class PineScriptService {

  /**
   * Convert visual rule-based strategy to PineScript
   */
  convertRulesToPineScript(rules: Rule[], options: PineScriptOptions): string {
    const { version = PineScriptVersion.V5, strategyName, description, includeComments = true } = options;
    
    // Start with script declaration and metadata
    let script = this.generateScriptHeader(version, strategyName, description, includeComments);
    
    // Add strategy initialization
    script += this.generateStrategyInit(version, strategyName, includeComments);
    
    // Process each rule
    for (const rule of rules) {
      script += this.convertRuleToPineScript(rule, version, includeComments);
    }
    
    return script;
  }

  /**
   * Convert Python strategy code to PineScript
   * This is a more complex task and would require actual code parsing
   * For now, we'll provide a simplified implementation
   */
  convertPythonToPineScript(pythonCode: string, options: PineScriptOptions): string {
    const { version = PineScriptVersion.V5, strategyName, description, includeComments = true } = options;
    
    // Start with script declaration
    let script = this.generateScriptHeader(version, strategyName, description, includeComments);
    
    // Add strategy initialization
    script += this.generateStrategyInit(version, strategyName, includeComments);
    
    // Add a comment about the conversion being limited
    if (includeComments) {
      script += "\n// This is a basic conversion from Python. Manual adjustments may be needed.\n";
      script += "// The original Python code is included as comments for reference.\n\n";
      
      // Add Python code as comments
      script += "/*\nOriginal Python code:\n";
      script += pythonCode.split("\n").map(line => "// " + line).join("\n");
      script += "\n*/\n\n";
    }
    
    // Extract basic patterns from Python code
    // This is very basic pattern matching and not a full code conversion
    
    // Look for common buy signals
    if (pythonCode.includes("buy") || pythonCode.includes("BUY")) {
      script += "\n// Basic buy condition based on pattern matching\n";
      script += "if (ta.crossover(close, ta.sma(close, 20)))\n";
      script += "    strategy.entry(\"Buy\", strategy.long)\n";
    }
    
    // Look for common sell signals
    if (pythonCode.includes("sell") || pythonCode.includes("SELL")) {
      script += "\n// Basic sell condition based on pattern matching\n";
      script += "if (ta.crossunder(close, ta.sma(close, 20)))\n";
      script += "    strategy.close(\"Buy\")\n";
    }
    
    return script;
  }

  /**
   * Generate PineScript header with metadata
   */
  private generateScriptHeader(version: PineScriptVersion, strategyName: string, description?: string, includeComments = true): string {
    const versionString = version === PineScriptVersion.V5 ? "5" : "4";
    let header = `//@version=${versionString}\n`;
    
    if (version === PineScriptVersion.V5) {
      header += `strategy("${strategyName}"`;
      if (description) {
        header += `, description="${description}"`;
      }
      header += ", overlay=true)\n\n";
    } else {
      header += `study("${strategyName}"`;
      if (description) {
        header += `, description="${description}"`;
      }
      header += ", overlay=true)\n\n";
    }
    
    if (includeComments) {
      header += "// Generated by Strategiz.io\n";
      header += `// Strategy Name: ${strategyName}\n`;
      if (description) {
        header += `// Description: ${description}\n`;
      }
      header += "// Date: " + new Date().toISOString().split('T')[0] + "\n\n";
    }
    
    return header;
  }

  /**
   * Generate strategy initialization code
   */
  private generateStrategyInit(version: PineScriptVersion, strategyName: string, includeComments = true): string {
    let init = "";
    
    if (includeComments) {
      init += "// Strategy initialization\n";
    }
    
    if (version === PineScriptVersion.V5) {
      init += "// Strategy settings\n";
      init += "startDate = input.time(timestamp(\"2020-01-01\"), title=\"Start Date\")\n";
      init += "endDate = input.time(timestamp(\"2023-01-01\"), title=\"End Date\")\n";
      init += "inDateRange = time >= startDate and time <= endDate\n\n";
    } else {
      init += "// Strategy settings\n";
      init += "startDate = input(timestamp(\"2020-01-01\"), title=\"Start Date\")\n";
      init += "endDate = input(timestamp(\"2023-01-01\"), title=\"End Date\")\n";
      init += "inDateRange = time >= startDate and time <= endDate\n\n";
    }
    
    return init;
  }

  /**
   * Convert a single rule to PineScript
   */
  private convertRuleToPineScript(rule: Rule, version: PineScriptVersion, includeComments = true): string {
    let ruleScript = "";
    
    if (includeComments) {
      ruleScript += `\n// Rule: ${rule.trigger}\n`;
    }
    
    // Create condition expressions
    const conditionExpressions = rule.conditions.map(condition => {
      return this.convertConditionToPineScript(condition, version);
    });
    
    // Combine conditions with AND
    const combinedConditions = conditionExpressions.join(" and ");
    
    // Generate entry/exit code based on actions
    const actionScripts = rule.actions.map(action => {
      return this.convertActionToPineScript(action, version);
    });
    
    // Create the full rule
    ruleScript += `if (${combinedConditions} and inDateRange)\n`;
    actionScripts.forEach((actionScript: string) => {
      ruleScript += `    ${actionScript}\n`;
    });
    
    return ruleScript;
  }

  /**
   * Convert a condition to PineScript expression
   */
  private convertConditionToPineScript(condition: Condition, version: PineScriptVersion): string {
    const { subject, indicator, operator, value } = condition;
    
    // Get the appropriate ticker symbol
    const ticker = this.getTickerSymbol(subject);
    
    // Build the indicator expression
    let indicatorExpr = "";
    
    // Parse different indicators
    switch (indicator.toLowerCase()) {
      case "price":
        indicatorExpr = "close";
        break;
      case "rsi":
        indicatorExpr = version === PineScriptVersion.V5 
          ? `ta.rsi(close, 14)` 
          : `rsi(close, 14)`;
        break;
      case "macd":
        // In real implementation, you'd need more complex logic for MACD
        indicatorExpr = version === PineScriptVersion.V5 
          ? `ta.macd(close, 12, 26, 9)[0]` 
          : `macd(close, 12, 26, 9)`;
        break;
      case "moving average":
        indicatorExpr = version === PineScriptVersion.V5 
          ? `ta.sma(close, 20)` 
          : `sma(close, 20)`;
        break;
      case "volume":
        indicatorExpr = "volume";
        break;
      default:
        indicatorExpr = "close"; // Default to price
    }
    
    // Build the operator expression
    let operatorExpr = "";
    
    switch (operator) {
      case "equals":
        operatorExpr = "==";
        break;
      case "greater than":
        operatorExpr = ">";
        break;
      case "less than":
        operatorExpr = "<";
        break;
      case "crosses above":
        return version === PineScriptVersion.V5 
          ? `ta.crossover(${indicatorExpr}, ${value})` 
          : `crossover(${indicatorExpr}, ${value})`;
      case "crosses below":
        return version === PineScriptVersion.V5 
          ? `ta.crossunder(${indicatorExpr}, ${value})` 
          : `crossunder(${indicatorExpr}, ${value})`;
      default:
        operatorExpr = "==";
    }
    
    // Return the complete condition
    return `${indicatorExpr} ${operatorExpr} ${value}`;
  }

  /**
   * Convert an action to PineScript statement
   */
  private convertActionToPineScript(action: Action, version: PineScriptVersion): string {
    const { type, asset, quantity, price } = action;
    
    // Build the entry/exit code based on action type
    switch (type.toUpperCase()) {
      case "BUY":
        return `strategy.entry("Buy ${asset}", strategy.long, qty=${quantity})`;
      case "SELL":
        return `strategy.close("Buy ${asset}")`;
      case "LIMIT BUY":
        if (price) {
          return `strategy.entry("Limit Buy ${asset}", strategy.long, limit=${price}, qty=${quantity})`;
        }
        return `strategy.entry("Limit Buy ${asset}", strategy.long, qty=${quantity})`;
      case "LIMIT SELL":
        if (price) {
          return `strategy.close("Buy ${asset}", limit=${price})`;
        }
        return `strategy.close("Buy ${asset}")`;
      default:
        return `// Unsupported action: ${type}`;
    }
  }

  /**
   * Get the appropriate ticker symbol for TradingView
   */
  private getTickerSymbol(asset: string): string {
    // Map common crypto symbols to TradingView tickers
    const mapping: Record<string, string> = {
      "BTC": "BTCUSD",
      "ETH": "ETHUSD",
      "LTC": "LTCUSD",
      "SOL": "SOLUSD",
      "ADA": "ADAUSD",
      "AVAX": "AVAXUSD",
      "DOT": "DOTUSD",
      "MATIC": "MATICUSD",
      "XRP": "XRPUSD",
      "DOGE": "DOGEUSD"
    };
    
    return mapping[asset] || asset;
  }
}

export const pineScriptService = new PineScriptService();
